import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingContactWidget from "@/components/FloatingContactWidget";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Shield, FileText, Download, CheckCircle2, AlertTriangle, FileCheck, Lock, Database } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const downloadSchema = z.object({
  name: z.string().trim().min(2, "Name erforderlich").max(100),
  email: z.string().trim().email("Ungültige E-Mail").max(255),
  practiceType: z.string().min(1, "Praxistyp erforderlich"),
  acceptsPrivacy: z.boolean().refine(val => val === true, "Datenschutz muss akzeptiert werden")
});

type DownloadForm = z.infer<typeof downloadSchema>;

const DsgvoComplianceCenter = () => {
  const [activeTab, setActiveTab] = useState("checklisten");
  const [downloadedItems, setDownloadedItems] = useState<string[]>([]);
  
  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm<DownloadForm>({
    resolver: zodResolver(downloadSchema)
  });

  const handleDownload = async (data: DownloadForm, itemType: string, fileName: string) => {
    try {
      // Lead speichern
      const { error } = await supabase.from('checklist_leads').insert({
        name: data.name,
        email: data.email,
        practice_type: data.practiceType,
        accepts_privacy: data.acceptsPrivacy
      });
      
      if (error) throw error;
      
      // Download markieren
      setDownloadedItems([...downloadedItems, fileName]);
      toast.success(`${fileName} wird heruntergeladen. Vielen Dank!`);
      reset();
      
      // Hier würde der eigentliche Download stattfinden
      // window.open(`/downloads/${fileName}.pdf`, '_blank');
      
    } catch (error) {
      console.error('Error:', error);
      toast.error("Fehler beim Herunterladen. Bitte versuchen Sie es erneut.");
    }
  };

  const checklists = [
    {
      title: "DSGVO-Compliance-Checkliste für Arztpraxen",
      description: "Vollständige Checkliste mit allen Pflichten nach DSGVO Art. 32 - von Verarbeitungsverzeichnis bis TOMs",
      icon: FileCheck,
      fileName: "dsgvo-compliance-checkliste-arztpraxen"
    },
    {
      title: "Cyber-Notfallplan-Vorlage",
      description: "Schritt-für-Schritt-Anleitung für den Ernstfall: Was tun bei Ransomware, Datenpanne oder IT-Ausfall?",
      icon: AlertTriangle,
      fileName: "cyber-notfallplan-vorlage"
    },
    {
      title: "Backup-Strategie 3-2-1-Regel",
      description: "Praxiserprobte Backup-Konzepte mit konkreten Tool-Empfehlungen und Wiederherstellungsplan",
      icon: Database,
      fileName: "backup-strategie-321-regel"
    },
    {
      title: "Mitarbeiter-Schulungsplan Cybersecurity",
      description: "Jahresplan für Cyber-Awareness-Trainings mit Phishing-Simulationen und Praxis-Szenarien",
      icon: Shield,
      fileName: "mitarbeiter-schulungsplan-cyber"
    }
  ];

  const templates = [
    {
      title: "Auftragsverarbeitungsvertrag (AVV) Muster",
      description: "Rechtssichere AVV-Vorlage für IT-Dienstleister, Cloud-Anbieter und Abrechnungsstellen",
      icon: FileText,
      fileName: "avv-muster-arztpraxen"
    },
    {
      title: "Technische und Organisatorische Maßnahmen (TOMs)",
      description: "Vollständiges TOMs-Dokument für Arztpraxen mit allen 8 Schutzziel-Kategorien",
      icon: Lock,
      fileName: "toms-vorlage-arztpraxen"
    },
    {
      title: "Datenschutz-Folgenabschätzung (DSFA) Template",
      description: "Strukturierte Vorlage zur Risikobewertung bei neuen Verarbeitungstätigkeiten",
      icon: FileCheck,
      fileName: "dsfa-template-arztpraxen"
    },
    {
      title: "Datenpannen-Meldung Vorlage",
      description: "Vorbereite Formulare für Meldung an Aufsichtsbehörde und Patientenbenachrichtigung",
      icon: AlertTriangle,
      fileName: "datenpannen-meldung-vorlage"
    }
  ];

  const faqs = [
    {
      question: "Welche DSGVO-Pflichten haben Arztpraxen?",
      answer: "Arztpraxen müssen: Verarbeitungsverzeichnis führen, TOMs dokumentieren, AVVs mit Dienstleistern abschließen, Datenschutz-Folgenabschätzungen durchführen, Datenpannen melden (72h), Betroffenenrechte erfüllen und ggf. Datenschutzbeauftragten bestellen (ab 20 Mitarbeiter)."
    },
    {
      question: "Braucht jede Arztpraxis einen Datenschutzbeauftragten?",
      answer: "Pflicht ab 20 Mitarbeitern, die regelmäßig mit personenbezogenen Daten arbeiten. Auch kleinere Praxen sollten externen DSB erwägen - Bußgelder bei fehlendem DSB können bis 50.000€ betragen."
    },
    {
      question: "Was sind Technische und Organisatorische Maßnahmen (TOMs)?",
      answer: "TOMs sind konkrete Sicherheitsmaßnahmen zum Schutz personenbezogener Daten: Zutrittskontrolle (Alarmanlage), Zugangskontrolle (Passwörter), Zugriffskontrolle (Berechtigungen), Weitergabekontrolle (Verschlüsselung), Eingabekontrolle (Protokollierung), etc."
    },
    {
      question: "Wie hoch sind DSGVO-Bußgelder für Arztpraxen?",
      answer: "Bis zu 20 Mio. € oder 4% des Jahresumsatzes. Praxen zahlen typisch: Fehlender Datenschutzbeauftragter 10-50k€, fehlende TOMs 20-100k€, Datenpanne ohne Meldung 50-200k€, schwere Datenschutzverletzung 100k-1Mio€."
    },
    {
      question: "Was muss bei Datenpanne getan werden?",
      answer: "1) Datenpanne dokumentieren (wann, was, wie viele), 2) Aufsichtsbehörde melden binnen 72h, 3) Bei hohem Risiko: Betroffene informieren, 4) Ursache beheben, 5) Maßnahmen dokumentieren. Versäumte 72h-Frist erhöht Bußgeld erheblich!"
    },
    {
      question: "Sind Cloud-Backups DSGVO-konform?",
      answer: "Ja, wenn: 1) AVV mit Cloud-Anbieter abgeschlossen, 2) Ende-zu-Ende-Verschlüsselung, 3) Server in EU/EWR, 4) Dokumentation der TOMs. Anbieter wie Microsoft 365, Google Workspace oder spezialisierte Medizin-Clouds (CompuGroup) bieten konforme Lösungen."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>DSGVO-Compliance Center | Checklisten & Vorlagen für Arztpraxen</title>
        <meta name="description" content="Kostenlose DSGVO-Checklisten, AVV-Vorlagen, TOMs-Dokumentation und Datenschutz-Tools für Arztpraxen. Jetzt herunterladen und DSGVO-konform werden!" />
        <link rel="canonical" href="https://praxis-versichert.de/dsgvo-compliance-center" />
      </Helmet>

      <Navigation />
      
      <main className="flex-grow">
        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-primary/10 via-background to-accent/10 border-b">
          <div className="container">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <Shield className="h-5 w-5" />
                <span className="font-semibold">DSGVO-Compliance Center</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Kostenlose DSGVO-Tools für Arztpraxen
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Checklisten, Vorlagen und Muster-Dokumente für rechts konforme Datenschutz-Compliance. Von Datenschutzexperten für medizinische Einrichtungen erstellt.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span>DSGVO-konform</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span>Praxis-erprobt</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span>Sofort einsetzbar</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="container">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-6xl mx-auto">
              <TabsList className="grid w-full grid-cols-3 mb-8">
                <TabsTrigger value="checklisten">Checklisten</TabsTrigger>
                <TabsTrigger value="vorlagen">Vorlagen & Muster</TabsTrigger>
                <TabsTrigger value="faq">FAQ</TabsTrigger>
              </TabsList>

              {/* Checklisten */}
              <TabsContent value="checklisten">
                <div className="grid md:grid-cols-2 gap-6">
                  {checklists.map((item, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-primary/10">
                            <item.icon className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-lg mb-2">{item.title}</CardTitle>
                            <CardDescription>{item.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={handleSubmit((data) => handleDownload(data, 'checkliste', item.fileName))}>
                          <div className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor={`${item.fileName}-name`}>Name *</Label>
                                <Input id={`${item.fileName}-name`} {...register("name")} placeholder="Dr. Mustermann" />
                                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name.message}</p>}
                              </div>
                              <div>
                                <Label htmlFor={`${item.fileName}-email`}>E-Mail *</Label>
                                <Input id={`${item.fileName}-email`} type="email" {...register("email")} placeholder="praxis@beispiel.de" />
                                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email.message}</p>}
                              </div>
                            </div>
                            <div>
                              <Label htmlFor={`${item.fileName}-practice`}>Praxistyp *</Label>
                              <Input id={`${item.fileName}-practice`} {...register("practiceType")} placeholder="z.B. Hausarztpraxis" />
                              {errors.practiceType && <p className="text-xs text-destructive mt-1">{errors.practiceType.message}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <input type="checkbox" id={`${item.fileName}-privacy`} onChange={(e) => setValue("acceptsPrivacy", e.target.checked)} className="rounded" />
                              <Label htmlFor={`${item.fileName}-privacy`} className="text-xs cursor-pointer">
                                Ich akzeptiere die Datenschutzerklärung *
                              </Label>
                            </div>
                            {errors.acceptsPrivacy && <p className="text-xs text-destructive">{errors.acceptsPrivacy.message}</p>}
                            <Button type="submit" className="w-full">
                              <Download className="mr-2 h-4 w-4" />
                              Jetzt kostenlos herunterladen
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Vorlagen */}
              <TabsContent value="vorlagen">
                <div className="grid md:grid-cols-2 gap-6">
                  {templates.map((item, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-primary/10">
                            <item.icon className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-lg mb-2">{item.title}</CardTitle>
                            <CardDescription>{item.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={handleSubmit((data) => handleDownload(data, 'vorlage', item.fileName))}>
                          <div className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor={`${item.fileName}-name`}>Name *</Label>
                                <Input id={`${item.fileName}-name`} {...register("name")} placeholder="Dr. Mustermann" />
                                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name.message}</p>}
                              </div>
                              <div>
                                <Label htmlFor={`${item.fileName}-email`}>E-Mail *</Label>
                                <Input id={`${item.fileName}-email`} type="email" {...register("email")} placeholder="praxis@beispiel.de" />
                                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email.message}</p>}
                              </div>
                            </div>
                            <div>
                              <Label htmlFor={`${item.fileName}-practice`}>Praxistyp *</Label>
                              <Input id={`${item.fileName}-practice`} {...register("practiceType")} placeholder="z.B. Zahnarztpraxis" />
                              {errors.practiceType && <p className="text-xs text-destructive mt-1">{errors.practiceType.message}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <input type="checkbox" id={`${item.fileName}-privacy`} onChange={(e) => setValue("acceptsPrivacy", e.target.checked)} className="rounded" />
                              <Label htmlFor={`${item.fileName}-privacy`} className="text-xs cursor-pointer">
                                Ich akzeptiere die Datenschutzerklärung *
                              </Label>
                            </div>
                            {errors.acceptsPrivacy && <p className="text-xs text-destructive">{errors.acceptsPrivacy.message}</p>}
                            <Button type="submit" className="w-full">
                              <Download className="mr-2 h-4 w-4" />
                              Vorlage herunterladen
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* FAQ */}
              <TabsContent value="faq">
                <Card>
                  <CardHeader>
                    <CardTitle>Häufige Fragen zu DSGVO-Compliance</CardTitle>
                    <CardDescription>Antworten auf die wichtigsten Datenschutz-Fragen für Arztpraxen</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      {faqs.map((faq, idx) => (
                        <AccordionItem key={idx} value={`faq-${idx}`}>
                          <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                          <AccordionContent className="text-muted-foreground whitespace-pre-line">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-primary/10 via-background to-accent/10 border-t">
          <div className="container text-center">
            <h2 className="text-3xl font-bold mb-4">Brauchen Sie persönliche Unterstützung?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Unsere Datenschutz- und Cyber-Experten beraten Sie gerne zu DSGVO-Compliance, Cyberversicherung und IT-Sicherheit für Ihre Praxis.
            </p>
            <Button size="lg" asChild>
              <a href="/termin-buchen">Kostenlose Beratung vereinbaren</a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingContactWidget />
    </div>
  );
};

export default DsgvoComplianceCenter;
